import { AvatarVariant, IconType, PersonAvatarVariant } from '@factorialco/f0-react'
import { ExternalLink, Pencil, Plus, PushPin, Settings } from '@factorialco/f0-react/icons/app'
import { PostsGroupHeaderQuery } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import { buildAvatarUrl } from 'modules/posts/lib/buildAvatarUrl'

type MetadataAction = {
  icon: IconType
  label: string
  onClick: () => void
}

type MetadataItemValue =
  | {
      type: 'list'
      variant: 'person'
      avatars: PersonAvatarVariant[]
    }
  | {
      type: 'avatar'
      variant: AvatarVariant
      text: string
    }

type MetadataItem = {
  label: string
  value: MetadataItemValue
  actions?: MetadataAction[]
}

type Props = {
  group: PostsGroupHeaderQuery['posts']['group']
  totalPinnedPostsCount: number
  isPostPinningEnabled: boolean
  isDynamicCommunitiesEnabled?: boolean
  setIsMembersModalOpen: (isOpen: boolean) => void
  onPinnedClick?: () => void
}

export const buildCommunityHeaderProps = ({
  group,
  totalPinnedPostsCount,
  isPostPinningEnabled = false,
  isDynamicCommunitiesEnabled = false,
  setIsMembersModalOpen,
  onPinnedClick,
}: Props) => {
  if (!group) {
    return {
      title: i18n.t('posts.communities.feed'),
      description: '',
      metadata: [],
      primaryAction: null,
      secondaryActions: [],
      otherActions: [],
    }
  }

  const isDefaultGroup = group.default
  const canLeaveGroup = group._policies?.leave?.can && !isDefaultGroup
  const canUpdateGroup = group._policies?.update.can && group.isMember
  const canManageMembers = canUpdateGroup && !isDefaultGroup
  const canDeleteGroup = canUpdateGroup && !isDefaultGroup

  const secondaryActions = [
    canUpdateGroup && {
      label: i18n.t('posts.communities.edit_group'),
      icon: Pencil,
      key: 'edit-community',
    },
  ].filter(Boolean)

  const otherActions = [
    canManageMembers && {
      label: i18n.t('posts.communities.add_members'),
      key: 'add-members',
    },
    canManageMembers && {
      label: i18n.t('posts.communities.remove_members'),
      key: 'remove-members',
    },
    canLeaveGroup && {
      label: i18n.t('posts.communities.leave_group'),
      key: 'leave-group',
    },
    canDeleteGroup && {
      label: i18n.t('posts.communities.delete_group'),
      key: 'delete-community',
      critical: true,
    },
  ].filter(Boolean)

  const primaryActionItems = [
    {
      label: i18n.t('posts.communities.invite_people'),
      value: 'invite-people',
    },
    isDynamicCommunitiesEnabled && {
      label: i18n.t('posts.communities.set_automatic_invites'),
      value: 'set-automatic-invites',
      icon: Settings,
      description: i18n.t('posts.communities.set_automatic_invites_description'),
    },
  ].filter(Boolean)

  let primaryAction = null

  if (!isDefaultGroup) {
    if (!group.isMember) {
      primaryAction = {
        label: i18n.t('dashboard.groups.join.submit'),
        icon: Plus,
        key: 'join-group',
      }
    } else if (canUpdateGroup && primaryActionItems.length > 1) {
      primaryAction = {
        items: primaryActionItems,
        key: 'primary-dropdown',
      }
    } else if (canUpdateGroup) {
      primaryAction = {
        label: i18n.t('posts.communities.invite_people'),
        icon: Plus,
        key: 'invite-people',
      }
    }
  }

  const allMembersAvatars: PersonAvatarVariant[] = group.employees.map((employee) => ({
    firstName: employee.firstName ?? '',
    lastName: employee.lastName ?? '',
    type: 'person' as const,
    src: buildAvatarUrl(employee.avatar),
  }))

  const metadata: MetadataItem[] = [
    {
      label: i18n.t('posts.communities.members'),
      value: {
        type: 'list',
        variant: 'person',
        avatars: allMembersAvatars,
      },
      actions: [
        {
          label: i18n.t('posts.communities.members'),
          icon: ExternalLink,
          onClick: () => setIsMembersModalOpen(true),
        },
      ],
    },
  ]

  if (totalPinnedPostsCount > 0 && isPostPinningEnabled) {
    metadata.push({
      label: i18n.t('posts.communities.pinned'),
      value: {
        text: String(totalPinnedPostsCount),
        type: 'avatar',
        variant: {
          icon: PushPin,
          type: 'icon',
        },
      },
      actions: onPinnedClick
        ? [
            {
              label: i18n.t('posts.communities.pinned'),
              icon: ExternalLink,
              onClick: onPinnedClick,
            },
          ]
        : undefined,
    })
  }

  return {
    title: group.title ?? '',
    description: group.description && group.description !== '-' ? group.description : '',
    status: {
      label: '',
      text: i18n.t('posts.communities.public'),
      variant: 'neutral' as const,
    },
    metadata,
    primaryAction,
    secondaryActions,
    otherActions,
  }
}
